import React, { useState, useEffect, Suspense } from 'react';
import { Routes, Route, Link } from 'react-router-dom';
import { Analytics } from '@vercel/analytics/react';
import styled from 'styled-components';
import './App.css';
import { ThemeProvider, useTheme } from './context/ThemeContext';
import ThemeToggle from './components/ThemeToggle';
import BlackScholesCalculator from './components/black-scholes/BlackScholesCalculator';
import OptionsWatchlist from './components/options-watchlist/OptionsWatchlist';

// Components are imported from their respective files

const Container = styled.div`
  background-color: ${props => props.isDarkMode ? '#333' : '#f5f5f5'};
  color: ${props => props.isDarkMode ? '#f5f5f5' : '#333'};
`;
const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  width: 100%;
  background-color: var(--bg-color, #f5f5f5);
  color: var(--text-color, #333);
  font-size: 1.2rem;
  transition: background-color 0.3s ease, color 0.3s ease;
`;

const Nav = styled.nav`
  background-color: var(--nav-bg-color, #2a5298);
  color: white;
  padding: 1rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 0;
  z-index: 1000;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  transition: background-color 0.3s ease;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
    padding: 0.75rem;
    gap: 0.75rem;
  }

  h1 {
    margin: 0;
    font-size: 1.5rem;

    @media (max-width: 768px) {
      font-size: 1.25rem;
    }
  }
`;

const NavLinks = styled.div`
  display: flex;
  gap: 1.5rem;
  align-items: center;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    width: 100%;
    gap: 0.75rem;
  }
`;

const StyledLink = styled(Link)`
  color: white;
  text-decoration: none;
  font-weight: bold;
  font-size: 1.1rem;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  transition: background-color 0.3s;
  white-space: nowrap;

  &:hover {
    background-color: #3a62a7;
  }

  &.active {
    background-color: #4a72b7;
  }

  @media (max-width: 768px) {
    font-size: 0.95rem;
    padding: 0.4rem 0.8rem;
    width: 100%;
    text-align: center;
  }
`;

const ReportIssueButton = styled.a`
  color: #324F94;
  text-decoration: none;
  font-weight: bold;
  font-size: 1.1rem;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  background-color: #F5F5F5; // Darker grey background
  margin-left: 1.5rem;
  transition: background-color 0.3s;
  display: inline-flex;
  align-items: center;
  border: none;
  outline: none;
  cursor: pointer;

  &:hover {
    background-color: #343a40; // Even darker grey on hover
    text-decoration: none;
  }

  @media (max-width: 768px) {
    margin-left: 0;
    margin-top: 1rem;
  }
`;

const MainContent = styled.main`
  padding: 1rem;
  min-height: calc(100vh - 60px);
  background-color: var(--bg-color, #f5f5f5);
  color: var(--text-color, #333);
  max-width: 100vw;
  overflow-x: hidden;
  transition: background-color 0.3s ease, color 0.3s ease;

  @media (max-width: 768px) {
    padding: 0.5rem;
  }
`;

const BrandContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;





function AppContent() {
  const { theme } = useTheme();
  const isDarkMode = theme.mode === 'dark';

  return (
    <div className="App">
      <Nav>
        <BrandContainer>
          <h1>Options Area</h1>
          <ThemeToggle />
        </BrandContainer>
        <NavLinks>
          <StyledLink to="/">Black-Scholes Calculator</StyledLink>
          <StyledLink to="/watchlist">Options Watchlist</StyledLink>
          <ReportIssueButton
            href="https://github.com/Justinjiang545/options-area-issue/issues"
            target="_blank"
            rel="noopener noreferrer"
          >
            Report an issue
          </ReportIssueButton>
        </NavLinks>
      </Nav>
      <MainContent>
        <Suspense fallback={<LoadingSpinner>Loading...</LoadingSpinner>}>
          <Routes>
            <Route path="/" element={<BlackScholesCalculator />} />
            <Route path="/watchlist" element={<OptionsWatchlist />} />
          </Routes>
        </Suspense>
      </MainContent>
      <Analytics />
    </div>
  );
}

function App() {
  return (
    <AppContent />
  );
}

export default App;
